// Trigger deploy Vercel - utente kipifyapp@gmail.com

// Fix_Custom_Formula_Value_Type
import React, { useMemo, useState } from 'react';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { Box, TablePagination, Typography } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useFormContext } from 'react-hook-form';
import { ReactComponent as DeleteIcon } from 'src/icons/delete.svg';
import { ReactComponent as DragIndicatorIcon } from 'src/icons/dragIndicator.svg';
import { ReactComponent as FacebookIcon } from 'src/icons/facebook.svg';
import { ReactComponent as GoogleAdsIcon } from 'src/icons/googleAds.svg';
import { ReactComponent as GoogleAnalyticsIcon } from 'src/icons/googleAnalytics.svg';
import { ReactComponent as GoogleSearchConsoleIcon } from 'src/icons/googleSearchConsole.svg';
import { ReactComponent as ShopifyIcon } from 'src/icons/shopify.svg';
import palette from 'src/theme/palette';

import { useConnectors } from '../../../../providers/connectors/hook/useConnectors';
import { formatCustomReportCellValue } from '../../../DatabaseConnector/formatDatabaseConnectorMetricValue';
import {
  ConnectorType,
  Metric,
  ValueType,
  MetricsByConnectorType,
  MetricFormatType,
  Query,
} from '../../../../utils/gql/types';
import {
  getCorrectCurrencyFormat,
  getCorrectPercentageFormat,
  getCorrectNumberFormat,
  getCorrectDurationFormat,
} from '../../../../utils/stringUtils';
import { notApplicableValue } from '../../constants';
import {
  DashboardForm,
  FrontendFilterType,
  InputName,
  TableRowsType,
} from '../../types';
import {
  buildCompareRowLookup,
  buildCompareSplitLookupKey,
  normalizeVendorBrandValue,
} from '../../compareUtils';
import {
  cellsMatchColumnId,
  checkValueMatchFilters,
  getTitle,
} from '../../utils';

import {
  CustomBodyTableCell,
  CustomHeadTableCell,
  DeleteIconButton,
  DragIndicatorWrapper,
  SortIconWrapper,
  Title,
  Wrapper,
  DropZoneIndicator,
  DragShadow,
} from './styled';
import { DashboardTableProps } from './types';
import { useQuery } from '@apollo/client';
import { GET_CUSTOM_FORMULAS } from '../../../../utils/gql/queries';
import { useDragAndDrop } from '../../../../hooks/useDragAndDrop';

export function CustomTable({
  metricsIsShown,
  rows,
  columnOrder,
  changeColumnOrder,
  sortConfig: externalSortConfig,
  onSortConfigChange,
  compareDateRange,
  compareData,
  kipifyRowsForTotals,
}: DashboardTableProps) {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  // Indici O(1) per match compare (Facebook/GSC con migliaia di righe altrimenti congelano).
  const gscCompareRows = Array.isArray(compareData?.googleSearchConsole)
    ? (compareData?.googleSearchConsole as any[])
    : null;
  const facebookCompareRows = Array.isArray(compareData?.facebook)
    ? (compareData?.facebook as any[])
    : null;
  const gscCompareLookup = useMemo(
    () => buildCompareRowLookup(gscCompareRows),
    [gscCompareRows],
  );
  const facebookCompareLookup = useMemo(
    () => buildCompareRowLookup(facebookCompareRows),
    [facebookCompareRows],
  );
  // Hook per il drag and drop con effetti visivi
  const {
    dragState,
    handleDragStart,
    handleDrag,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    getColumnClassName,
  } = useDragAndDrop({
    onColumnReorder: (newOrder) => {
      // Rimuovo sortBySplitsAndMetrics per permettere il drag and drop libero
      changeColumnOrder(newOrder);
    },
    columnOrder,
    enableSound: false, // Disabilito il suono
    enableVibration: true,
  });
  type SortConfig = { columnId: string | null; direction: 'asc' | 'desc' };

  const [internalSortConfig, setInternalSortConfig] = useState<SortConfig>({
    columnId: null,
    direction: 'desc',
  });
  const sortConfig: SortConfig = externalSortConfig ?? internalSortConfig;
  const setSortConfigValue = (next: SortConfig) => {
    if (onSortConfigChange) {
      onSortConfigChange(next);
    } else {
      setInternalSortConfig(next);
    }
  };
  const updateSortConfig = (updater: React.SetStateAction<SortConfig>) => {
    setSortConfigValue(
      typeof updater === 'function' ? updater(sortConfig) : updater,
    );
  };

  const { setValue, watch, getValues } = useFormContext<DashboardForm>();

  const findColumnByColumnId = (
    cols: TableRowsType[] | undefined,
    targetColumnId: string | null | undefined,
  ): TableRowsType | undefined => {
    if (!targetColumnId) return undefined;
    return cols?.find(
      (col) => col && cellsMatchColumnId(targetColumnId, col.columnId),
    );
  };

  const findCellByColumnId = (
    cols: TableRowsType[] | undefined,
    targetColumnId: string | null | undefined,
  ): TableRowsType | undefined => {
    if (!targetColumnId) return undefined;
    return cols?.find(
      (cell) => cell && cellsMatchColumnId(targetColumnId, cell.columnId),
    );
  };

  const normalizeSplitValueForMatch = (key: string, value: string): string => {
    const k = String(key || '').toLowerCase();
    const v = String(value ?? '');
    if (!v) return '';

    // Stessa normalizzazione del join Vendor↔Brand (vedi compareUtils).
    if (
      k === 'vendor' ||
      k === 'product_brand' ||
      k === 'segments.product_brand' ||
      k.endsWith('.product_brand')
    ) {
      return normalizeVendorBrandValue(v);
    }

    if (k === 'product_type' || k === 'product_title') {
      return v
        .toLowerCase()
        .replace(/\s{2,}/g, ' ')
        .trim();
    }

    if (k === 'date') {
      return v.split('T')[0].trim();
    }

    if (k.includes('country')) {
      return v.trim().toUpperCase();
    }

    if (k === 'query' || k === 'page' || k === 'searchappearance') {
      return v.trim();
    }

    return v.trim();
  };

  const isMissingCompareRowEquivalentToZero = (
    cell: TableRowsType,
  ): boolean => {
    const hay = `${cell.id ?? ''} ${cell.title ?? ''}`.toLowerCase();

    if (cell.type === ConnectorType.GOOGlE_ANALYTICS4) {
      return /revenue|purchase|items|sessions|users|eventcount|views/.test(hay);
    }

    if (cell.type === ConnectorType.SHOPIFY) {
      return /sales|revenue|orders|customers|cost|refund|tax|shipping|discount|quantity/.test(
        hay,
      );
    }

    if (cell.type === ConnectorType.FACEBOOK) {
      return /spend|amount|impressions|clicks|reach|actions|conversions/.test(
        hay,
      );
    }

    if (cell.type === ConnectorType.GOOGLE_ADS) {
      return /cost|spend|impressions|clicks|conversions|conversion.*value|revenue/.test(
        hay,
      );
    }

    // Metriche DB tipicamente additive (count/sum): riga assente nel compare ≈ 0
    if (cell.type === ConnectorType.DATABASE) {
      return true;
    }

    return false;
  };

  // Funzione helper per calcolare la variazione percentuale (come in KPI)
  // IMPORTANTE: Non inventiamo mai valori. Se non c'è un valore di comparazione valido, restituiamo null.
  const calculateChange = (
    currentValue: number | null,
    previousValue: number | null,
    metricKey?: string,
    connectorType?: ConnectorType | string,
  ) => {
    // Se mancano i valori, non mostrare comparazione (non inventare)
    if (
      previousValue === null ||
      previousValue === undefined ||
      currentValue === null ||
      currentValue === undefined
    ) {
      return null;
    }

    // Metriche dove "più basso è meglio": logica invertita per colori e frecce
    // OAC, ACOS, ACOS_total, CAC: diminuzione = positivo (verde, freccia su), aumento = negativo (arancione/rosso, freccia giù)
    const isInvertedMetric =
      connectorType === ConnectorType.KIPIFY &&
      metricKey &&
      (metricKey === 'oac' ||
        metricKey === 'acos' ||
        metricKey === 'acos_total' ||
        metricKey === 'cac' ||
        metricKey === 'cac_new_customers');

    // Se il valore precedente è 0, la variazione percentuale è "infinita"/non definita.
    // Per non inventare un % finto, rendiamo esplicito il caso e mostriamo comunque il valore di confronto (0).
    if (previousValue === 0) {
      // Per metriche invertite: se currentValue > 0 è negativo (peggio), se currentValue = 0 è positivo (meglio)
      const rawIsPositive = currentValue >= 0;
      const isPositive = isInvertedMetric ? !rawIsPositive : rawIsPositive;
      return {
        percentage: null as number | null,
        value: previousValue,
        isPositive,
        isInfinite: true as const,
      };
    }

    // Calcola la percentuale solo se entrambi i valori sono validi e significativi
    const percentage = ((currentValue - previousValue) / previousValue) * 100;

    // Per metriche invertite: se percentage < 0 (diminuzione) è positivo, se percentage > 0 (aumento) è negativo
    const rawIsPositive = percentage >= 0;
    const isPositive = isInvertedMetric ? !rawIsPositive : rawIsPositive;

    return {
      percentage: Math.round(percentage * 100) / 100,
      value: previousValue,
      isPositive,
    };
  };

  // Funzione helper per trovare il valore di comparazione per una cella
  const findCompareValue = (cell: TableRowsType, row: TableRowsType[]) => {
    if (!compareData || !cell || cell.isSplit) {
      if (
        cell &&
        !cell.isSplit &&
        cell.type === ConnectorType.KIPIFY &&
        !compareData?.kipify
      ) {
        console.log('[KIPIFY_COMPARE_DEBUG] No compare data available', {
          hasCompareData: !!compareData,
          hasKipifyData: !!compareData?.kipify,
          cellId: cell.id,
        });
      }
      return null;
    }

    const connectorType = cell.type;
    let compareRows: any[] = [];

    if (connectorType === ConnectorType.SHOPIFY && compareData.shopify) {
      // getShopifyMetricValues restituisce { metricRows: [...], filtersOptions: [...] }
      const shopifyData = compareData.shopify;
      if (
        shopifyData &&
        typeof shopifyData === 'object' &&
        'metricRows' in shopifyData
      ) {
        compareRows = Array.isArray(shopifyData.metricRows)
          ? shopifyData.metricRows
          : [];
      } else if (Array.isArray(shopifyData)) {
        compareRows = shopifyData;
      } else {
        compareRows = [];
      }
    } else if (connectorType === ConnectorType.MAGENTO && compareData.magento) {
      const magentoData = compareData.magento;
      if (
        magentoData &&
        typeof magentoData === 'object' &&
        'metricRows' in magentoData
      ) {
        compareRows = Array.isArray(magentoData.metricRows)
          ? magentoData.metricRows
          : [];
      } else if (Array.isArray(magentoData)) {
        compareRows = magentoData;
      } else {
        compareRows = [];
      }
    } else if (
      connectorType === ConnectorType.FACEBOOK &&
      compareData.facebook
    ) {
      // getFacebookMetricsValues restituisce un array diretto di MetricValuesRow
      const facebookData = compareData.facebook;
      compareRows = Array.isArray(facebookData) ? facebookData : [];
    } else if (
      connectorType === ConnectorType.GOOGLE_ADS &&
      compareData.googleAds
    ) {
      // getGoogleAdsMetrics restituisce un array diretto di MetricValuesRow
      const googleAdsData = compareData.googleAds;
      if (Array.isArray(googleAdsData)) {
        compareRows = googleAdsData;
      } else {
        compareRows = [];
      }
    } else if (
      connectorType === ConnectorType.GOOGlE_ANALYTICS4 &&
      compareData.googleAnalytics4
    ) {
      // getGoogleAnalytics4Metrics restituisce un array diretto di MetricValuesRow
      const googleAnalyticsData = compareData.googleAnalytics4;
      if (Array.isArray(googleAnalyticsData)) {
        compareRows = googleAnalyticsData;
      } else {
        compareRows = [];
      }
    } else if (
      connectorType === ConnectorType.GOOGLE_SEARCH_CONSOLE &&
      compareData.googleSearchConsole
    ) {
      const gscData = compareData.googleSearchConsole;
      compareRows = Array.isArray(gscData) ? gscData : [];
    } else if (connectorType === ConnectorType.KIPIFY && compareData.kipify) {
      // getKipifyMetricsValues restituisce un array diretto di MetricValuesRow
      const kipifyData = compareData.kipify;
      if (Array.isArray(kipifyData)) {
        compareRows = kipifyData;
        console.log('[KIPIFY_COMPARE_DEBUG] Found compare rows', {
          count: compareRows.length,
          firstRow: compareRows[0],
          cellId: cell.id,
          allMetricKeys: compareRows.flatMap(
            (r: any) => r.metricValues?.map((mv: any) => mv.key) || [],
          ),
        });
      } else {
        console.log('[KIPIFY_COMPARE_DEBUG] Kipify data is not an array', {
          dataType: typeof kipifyData,
          data: kipifyData,
        });
        compareRows = [];
      }
    } else if (
      connectorType === ConnectorType.DATABASE &&
      compareData.database
    ) {
      const databaseData = compareData.database;
      compareRows = Array.isArray(databaseData) ? databaseData : [];
    } else if (
      (connectorType as any) === MetricsByConnectorType.CUSTOM_FORMULA &&
      (compareData as any)?.customFormula
    ) {
      // getCustomFormulaValues restituisce un array diretto di MetricValuesRow
      const customFormulaCompare = (compareData as any).customFormula;
      if (Array.isArray(customFormulaCompare)) {
        compareRows = customFormulaCompare;
      } else {
        compareRows = [];
      }
    }

    // Verifica che compareRows sia un array prima di usare .find
    if (!Array.isArray(compareRows) || compareRows.length === 0) {
      return null;
    }

    // Fast path O(1): GSC / Facebook con lookup pre-indicizzato (evita freeze).
    const fastLookup =
      connectorType === ConnectorType.GOOGLE_SEARCH_CONSOLE
        ? gscCompareLookup
        : connectorType === ConnectorType.FACEBOOK
          ? facebookCompareLookup
          : null;
    if (fastLookup && fastLookup.size > 0) {
      const splitCells = row.filter(
        (c) => c && c.isSplit && c.type === connectorType,
      );
      const lookupKey = buildCompareSplitLookupKey(
        splitCells.map((c) => ({
          key: c!.id,
          value: normalizeSplitValueForMatch(c!.id, String(c!.value ?? '')),
        })),
      );
      const hit = fastLookup.get(lookupKey);
      if (hit) {
        const mv = hit.metricValues?.find(
          (m: any) => m.key === cell.id || m.key === `metrics.${cell.id}`,
        );
        if (mv != null) {
          const n = Number(mv.value);
          return Number.isFinite(n) ? n : null;
        }
      }
      // Nessun match esatto: per clicks/impressions GSC manca ≈ 0 (come Facebook spend)
      if (
        connectorType === ConnectorType.GOOGLE_SEARCH_CONSOLE &&
        /^(clicks|impressions)$/i.test(String(cell.id || ''))
      ) {
        return 0;
      }
      if (
        connectorType === ConnectorType.FACEBOOK &&
        /spend|amount|impressions|clicks/i.test(String(cell.id || ''))
      ) {
        return 0;
      }
      return null;
    }

    // Estrai tutti gli split dalla riga corrente per fare match accurato
    // IMPORTANTE: per Google Ads e Kipify, gli split potrebbero avere nomi diversi
    // (es. product_type vs segments.product_type_l1), quindi dobbiamo matchare in modo più flessibile
    // Per Kipify, gli split potrebbero non essere presenti nelle righe di comparazione se le metriche sono aggregate
    // NB: quando ci sono più split "misti" (es. product_type di Shopify + user_country di Google Ads),
    // la riga contiene split con type diversi. Per il matching del confronto dobbiamo includere anche
    // split "condivisi" (product_type/product_title/date) anche se non hanno lo stesso type del connettore.
    const currentSplits = row
      .filter((c) => {
        if (!c || !c.isSplit) return false;
        if (c.type === connectorType) return true;

        // Split "cross-connector": possono essere creati/normalizzati (es. `kipify_country`)
        // o mostrati con un'icona diversa da quella del connettore della metrica.
        // Se li ignoriamo, il confronto matcha la "prima riga utile" e finisce per mostrare (0) o valori errati.
        const shared =
          c.id === 'product_type' ||
          c.id === 'product_title' ||
          c.id === 'product_brand' ||
          c.id === 'segments.product_brand' ||
          c.id === 'vendor' ||
          c.id === 'date' ||
          c.id === 'kipify_country';
        if (!shared) return false;

        // Questi split possono servire per matchare correttamente le righe di confronto anche
        // quando la metrica è di un altro connettore (es. Orders Shopify + Kipify Country).
        if (connectorType === ConnectorType.GOOGLE_ADS) return true;
        if (connectorType === ConnectorType.FACEBOOK) return true;
        if (connectorType === ConnectorType.KIPIFY) return true;
        if (connectorType === ConnectorType.SHOPIFY) return true;
        return false;
      })
      .map((c) => {
        // Normalizza le chiavi degli split per Google Ads (es. segments.product_type_l1 -> product_type)
        let normalizedKey = c.id;
        if (
          connectorType === ConnectorType.GOOGLE_ADS &&
          c.id.startsWith('segments.')
        ) {
          // Rimuovi il prefisso "segments." e normalizza
          normalizedKey = c.id.replace(/^segments\./, '').replace(/_l\d+$/, '');
        }
        // Per Kipify, alcuni split potrebbero essere condivisi con altri connettori (es. product_type)
        // quindi manteniamo la chiave originale per il matching
        return {
          key: normalizedKey,
          originalKey: c.id,
          value: normalizeSplitValueForMatch(
            normalizedKey,
            String(c.value ?? ''),
          ),
          type: c.type,
        };
      });

    // Considera SOLO gli split effettivamente presenti nel dataset di confronto.
    // Questo evita mismatch su Facebook quando la riga corrente contiene split “condivisi”
    // (es. date/product_type) ma Facebook non li restituisce nel periodo di confronto.
    const normalizeCompareSplitKey = (k: string): string => {
      const key = String(k || '');
      if (
        connectorType === ConnectorType.GOOGLE_ADS &&
        key.startsWith('segments.')
      ) {
        return key.replace(/^segments\./, '').replace(/_l\d+$/, '');
      }
      return key;
    };
    const compareSplitKeySet = new Set<string>();
    for (const r of compareRows) {
      const splits = (r as any)?.splitValues || [];
      for (const sv of splits) {
        const rawKey = String(sv?.key ?? '');
        if (!rawKey) continue;
        compareSplitKeySet.add(rawKey);
        compareSplitKeySet.add(normalizeCompareSplitKey(rawKey));
      }
    }
    const relevantCurrentSplits = currentSplits.filter(
      (s) =>
        compareSplitKeySet.has(s.key) || compareSplitKeySet.has(s.originalKey),
    );

    // --- ROAS (Kipify) è spesso derivato (revenue/spend) quando c'è join per product_type.
    // Il confronto va calcolato allo stesso modo sul periodo di comparazione.
    const isKipifyRoasCell =
      !cell.isSplit &&
      (cell.id === 'roas' || cell.id === 'kipify_roas') &&
      (cell.type === ConnectorType.KIPIFY ||
        (cell.type as any) === MetricsByConnectorType.KIPIFY);
    if (isKipifyRoasCell) {
      const shopifyRevenueCells = row.filter(
        (c) =>
          c &&
          !c.isSplit &&
          c.type === ConnectorType.SHOPIFY &&
          /gross.*sales|total.*sales|net.*sales|revenue|sales/i.test(
            c.id || c.title || '',
          ),
      );

      const spendCells = row.filter(
        (c) =>
          c &&
          !c.isSplit &&
          ((c.type === ConnectorType.FACEBOOK &&
            /spend|amount_spent|amount/i.test(c.id || c.title || '')) ||
            (c.type === ConnectorType.GOOGLE_ADS &&
              /cost|spend|advertising_spend/i.test(c.id || c.title || '')) ||
            ((c.type === ConnectorType.KIPIFY ||
              (c.type as any) === MetricsByConnectorType.KIPIFY) &&
              /total_marketing_spend/i.test(c.id || c.title || ''))),
      );

      let compareRevenue = 0;
      for (const revCell of shopifyRevenueCells) {
        const v = findCompareValue(revCell, row);
        if (v !== null && v !== undefined && !isNaN(Number(v)))
          compareRevenue += Number(v);
      }

      let compareSpend = 0;
      for (const sCell of spendCells) {
        const v = findCompareValue(sCell, row);
        if (v !== null && v !== undefined && !isNaN(Number(v)))
          compareSpend += Number(v);
      }

      if (compareSpend > 0) {
        const roas = compareRevenue / compareSpend;
        return isFinite(roas) ? Number(roas.toFixed(2)) : null;
      }

      return null;
    }

    // Cerca la riga corrispondente che ha:
    // 1. Gli stessi split (stesso tipo, stesso ID, stesso valore)
    // 2. La metrica richiesta
    // IMPORTANTE: per Google Ads, le metriche vengono mappate (es. spend -> metrics.cost_micros)
    // quindi dobbiamo matchare sia la chiave originale che quella mappata
    const metricKeysToMatch = [cell.id];

    // Per Kipify, le metriche sono semplici (roas, markup, acos, etc.) quindi matchano direttamente
    if (connectorType === ConnectorType.KIPIFY) {
      // Assicurati che le chiavi metriche siano corrette per Kipify
      // Le metriche Kipify sono: roas, markup, acos, acos_total, oac, cac, cac_new_customers, total_marketing_spend
      const kipifyMetricKey = cell.id;
      if (kipifyMetricKey && !metricKeysToMatch.includes(kipifyMetricKey)) {
        metricKeysToMatch.push(kipifyMetricKey);
      }

      // Log dettagliato per ROAS
      if (cell.id === 'roas') {
        console.log('[KIPIFY_ROAS_DEBUG] Looking for ROAS metric', {
          cellId: cell.id,
          metricKeysToMatch,
          compareRowsCount: compareRows.length,
          allAvailableKeys: compareRows.flatMap(
            (r: any) => r.metricValues?.map((mv: any) => mv.key) || [],
          ),
          currentSplits: row
            .filter((c) => c && c.isSplit && c.type === connectorType)
            .map((c) => `${c.id}=${c.value}`),
          allRowsWithSplits: compareRows.map((r: any) => ({
            splits:
              r.splitValues?.map((sv: any) => `${sv.key}=${sv.value}`) || [],
            metrics:
              r.metricValues?.map((mv: any) => `${mv.key}=${mv.value}`) || [],
          })),
          firstRowFull: compareRows[0],
        });
      }

      console.log('[KIPIFY_COMPARE_DEBUG] Looking for metric', {
        cellId: cell.id,
        metricKeysToMatch,
        compareRowsCount: compareRows.length,
        allAvailableKeys: compareRows.flatMap(
          (r: any) => r.metricValues?.map((mv: any) => mv.key) || [],
        ),
        currentSplits: row
          .filter((c) => c && c.isSplit && c.type === connectorType)
          .map((c) => `${c.id}=${c.value}`),
      });
    }

    if (connectorType === ConnectorType.GOOGLE_ADS) {
      // Reverse mapping per Google Ads: mappa le chiavi logiche alle chiavi GAQL
      const key = cell.id.toLowerCase();
      if (key === 'cost' || key === 'spend') {
        metricKeysToMatch.push(
          'metrics.cost_micros',
          'cost_micros',
          'metrics.cost',
          'cost',
        );
      } else if (key === 'impressions') {
        metricKeysToMatch.push('metrics.impressions', 'impressions');
      } else if (key === 'clicks') {
        metricKeysToMatch.push('metrics.clicks', 'clicks');
      } else if (key === 'conversions') {
        metricKeysToMatch.push('metrics.conversions', 'conversions');
      } else if (key === 'conversions_value' || key === 'revenue') {
        metricKeysToMatch.push(
          'metrics.conversions_value',
          'conversions_value',
          'metrics.revenue',
          'revenue',
        );
      } else if (key === 'roas') {
        metricKeysToMatch.push('metrics.roas', 'roas');
      } else if (key === 'average_cpc' || key === 'cpc') {
        metricKeysToMatch.push(
          'metrics.average_cpc',
          'average_cpc',
          'metrics.cpc',
          'cpc',
        );
      } else if (key === 'average_cpm' || key === 'cpm') {
        metricKeysToMatch.push(
          'metrics.average_cpm',
          'average_cpm',
          'metrics.cpm',
          'cpm',
        );
      } else if (key === 'ctr') {
        metricKeysToMatch.push('metrics.ctr', 'ctr');
      }
      // Aggiungi anche la versione con prefisso metrics. se non c'è già
      if (!cell.id.startsWith('metrics.')) {
        metricKeysToMatch.push(`metrics.${cell.id}`);
      }
      // Aggiungi anche la versione senza prefisso metrics. se c'è
      if (cell.id.startsWith('metrics.')) {
        metricKeysToMatch.push(cell.id.replace(/^metrics\./, ''));
      }
    }

    const compareRow = compareRows.find((r: any) => {
      // Verifica che la riga abbia la metrica richiesta (matcha tutte le varianti possibili)
      const hasMetric = r.metricValues?.some((mv: any) => {
        const matches =
          metricKeysToMatch.includes(mv.key) ||
          mv.key === cell.id ||
          (connectorType === ConnectorType.GOOGLE_ADS &&
            mv.key === `metrics.${cell.id}`);
        if (
          connectorType === ConnectorType.KIPIFY &&
          matches &&
          cell.id === 'roas'
        ) {
          console.log('[KIPIFY_ROAS_DEBUG] ROAS metric matched in row!', {
            cellId: cell.id,
            metricKey: mv.key,
            value: mv.value,
            rowSplits:
              r.splitValues?.map((sv: any) => `${sv.key}=${sv.value}`) || [],
          });
        }
        return matches;
      });
      if (!hasMetric) {
        // Debug temporaneo
        if (connectorType === ConnectorType.KIPIFY) {
          if (cell.id === 'roas') {
            console.log('[KIPIFY_ROAS_DEBUG] ROAS metric NOT found in row', {
              cellId: cell.id,
              metricKeysToMatch,
              availableKeys: r.metricValues?.map((mv: any) => mv.key) || [],
              rowSplits:
                r.splitValues?.map((sv: any) => `${sv.key}=${sv.value}`) || [],
              rowIndex: compareRows.indexOf(r),
              fullRow: r,
            });
          }
          console.log('[KIPIFY_COMPARE_DEBUG] Metric not found in row', {
            connectorType,
            cellId: cell.id,
            metricKeysToMatch,
            availableKeys: r.metricValues?.map((mv: any) => mv.key) || [],
            rowSplits:
              r.splitValues?.map((sv: any) => `${sv.key}=${sv.value}`) || [],
            rowIndex: compareRows.indexOf(r),
          });
        }
        return false;
      }

      // Se non ci sono split nella riga corrente, matcha qualsiasi riga con quella metrica
      if (relevantCurrentSplits.length === 0) return true;

      // Verifica che tutti gli split della riga corrente siano presenti nella riga di comparazione
      const compareSplits = (r.splitValues || []).map((sv: any) => {
        // Normalizza anche le chiavi degli split di comparazione per Google Ads
        let normalizedKey = sv.key;
        if (
          connectorType === ConnectorType.GOOGLE_ADS &&
          typeof sv.key === 'string' &&
          sv.key.startsWith('segments.')
        ) {
          normalizedKey = sv.key
            .replace(/^segments\./, '')
            .replace(/_l\d+$/, '');
        }
        return {
          key: normalizedKey,
          originalKey: sv.key,
          value: normalizeSplitValueForMatch(
            normalizedKey,
            String(sv.value ?? ''),
          ),
        };
      });

      // Per Kipify, se le metriche sono aggregate, potrebbero non avere split corrispondenti
      // In questo caso, proviamo a matchare solo per metrica (senza split)
      if (
        connectorType === ConnectorType.KIPIFY &&
        compareSplits.length === 0 &&
        relevantCurrentSplits.length > 0
      ) {
        // Se la riga di comparazione non ha split ma quella corrente sì,
        // potrebbe essere una metrica aggregata - matcha comunque per metrica
        return true;
      }

      // Match esatto: tutti gli split della riga corrente devono essere presenti nella riga di comparazione
      // Usa sia la chiave normalizzata che quella originale per il matching
      const allSplitsMatch = relevantCurrentSplits.every((currentSplit) =>
        compareSplits.some(
          (compareSplit: { key: string; originalKey: string; value: string }) =>
            (compareSplit.key === currentSplit.key ||
              compareSplit.originalKey === currentSplit.originalKey) &&
            compareSplit.value === currentSplit.value,
        ),
      );

      if (!allSplitsMatch && connectorType === ConnectorType.KIPIFY) {
        // Debug temporaneo per Kipify
        console.log('[KIPIFY_COMPARE_DEBUG] Splits mismatch', {
          connectorType,
          cellId: cell.id,
          currentSplits,
          compareSplits,
          rowSplits:
            r.splitValues?.map((sv: any) => `${sv.key}=${sv.value}`) || [],
        });
      }

      return allSplitsMatch;
    });

    if (compareRow) {
      // Cerca la metrica usando tutte le varianti possibili
      const compareMetric = compareRow.metricValues?.find(
        (mv: any) =>
          metricKeysToMatch.includes(mv.key) ||
          mv.key === cell.id ||
          (connectorType === ConnectorType.GOOGLE_ADS &&
            mv.key === `metrics.${cell.id}`),
      );
      if (compareMetric) {
        const numValue = Number(compareMetric.value);
        if (connectorType === ConnectorType.KIPIFY) {
          if (cell.id === 'roas') {
            console.log('[KIPIFY_ROAS_DEBUG] Found ROAS compare value!', {
              cellId: cell.id,
              metricKey: compareMetric.key,
              value: compareMetric.value,
              numValue,
              rowSplits:
                compareRow.splitValues?.map(
                  (sv: any) => `${sv.key}=${sv.value}`,
                ) || [],
            });
          }
          console.log('[KIPIFY_COMPARE_DEBUG] Found compare value', {
            cellId: cell.id,
            metricKey: compareMetric.key,
            value: compareMetric.value,
            numValue,
          });
        }
        return !isNaN(numValue) ? numValue : null;
      } else if (connectorType === ConnectorType.KIPIFY) {
        if (cell.id === 'roas') {
          console.log(
            '[KIPIFY_ROAS_DEBUG] ROAS metric found in row but not matching keys',
            {
              cellId: cell.id,
              metricKeysToMatch,
              availableMetrics:
                compareRow.metricValues?.map(
                  (mv: any) => `${mv.key}=${mv.value}`,
                ) || [],
              fullRow: compareRow,
            },
          );
        }
        console.log(
          '[KIPIFY_COMPARE_DEBUG] Metric found in row but not matching',
          {
            cellId: cell.id,
            metricKeysToMatch,
            availableMetrics:
              compareRow.metricValues?.map((mv: any) => mv.key) || [],
          },
        );
      }
    } else if (connectorType === ConnectorType.KIPIFY) {
      if (cell.id === 'roas') {
        console.log('[KIPIFY_ROAS_DEBUG] No compare row found for ROAS', {
          cellId: cell.id,
          compareRowsLength: compareRows.length,
          currentSplits,
          firstRowSplits:
            compareRows[0]?.splitValues?.map(
              (sv: any) => `${sv.key}=${sv.value}`,
            ) || [],
          firstRowMetrics:
            compareRows[0]?.metricValues?.map(
              (mv: any) => `${mv.key}=${mv.value}`,
            ) || [],
          allRowsInfo: compareRows.map((r: any, idx: number) => ({
            index: idx,
            splits:
              r.splitValues?.map((sv: any) => `${sv.key}=${sv.value}`) || [],
            metrics:
              r.metricValues?.map((mv: any) => `${mv.key}=${mv.value}`) || [],
          })),
        });
      }
      console.log('[KIPIFY_COMPARE_DEBUG] No compare row found', {
        cellId: cell.id,
        compareRowsLength: compareRows.length,
        currentSplits,
        firstRowSplits:
          compareRows[0]?.splitValues?.map(
            (sv: any) => `${sv.key}=${sv.value}`,
          ) || [],
      });
    }

    if (!compareRow && relevantCurrentSplits.length > 0) {
      return isMissingCompareRowEquivalentToZero(cell) ? 0 : null;
    }

    // Fallback: se non trova una riga con split corrispondenti, prova a matchare solo per metrica
    // MA SOLO quando i dati di confronto sono *aggregati* (cioè senza split).
    //
    // Perché: se il confronto restituisce split (es. kipify_country=Italy) ma per una riga corrente
    // non esiste il paese nel periodo di confronto, matchare “solo per metrica” farebbe mostrare
    // lo stesso valore (della prima riga) su TUTTE le righe → dato fuorviante.
    if (
      connectorType === ConnectorType.KIPIFY &&
      relevantCurrentSplits.length > 0
    ) {
      const compareRowsAreAggregated =
        compareSplitKeySet.size === 0 ||
        compareRows.every(
          (r: any) =>
            !Array.isArray(r?.splitValues) || r.splitValues.length === 0,
        );

      if (!compareRowsAreAggregated) return null;

      const fallbackRow = compareRows.find((r: any) => {
        const hasMetric = r.metricValues?.some(
          (mv: any) => metricKeysToMatch.includes(mv.key) || mv.key === cell.id,
        );
        return hasMetric;
      });

      if (fallbackRow) {
        const compareMetric = fallbackRow.metricValues?.find(
          (mv: any) => metricKeysToMatch.includes(mv.key) || mv.key === cell.id,
        );
        if (compareMetric) {
          const numValue = Number(compareMetric.value);
          if (connectorType === ConnectorType.KIPIFY) {
            console.log(
              '[KIPIFY_COMPARE_DEBUG] Using fallback match (no splits)',
              {
                cellId: cell.id,
                metricKey: compareMetric.key,
                value: compareMetric.value,
                numValue,
              },
            );
          }
          return !isNaN(numValue) ? numValue : null;
        }
      }
    }

    // Fallback aggiuntivo per Kipify: se non ci sono split nella riga corrente,
    // prova a matchare la prima riga che ha quella metrica (utile per metriche aggregate)
    // IMPORTANTE: questo è utile quando le metriche Kipify (roas, markup, acos) sono aggregate
    // e non hanno split corrispondenti
    if (connectorType === ConnectorType.KIPIFY) {
      // Per Kipify, le metriche come ROAS sono spesso aggregate e potrebbero non avere split corrispondenti
      // Prova a matchare in questo ordine:
      // 1. Riga con split corrispondenti E metrica
      // 2. Riga con metrica (anche senza split corrispondenti) - utile per metriche aggregate
      const anyRowWithMetric = compareRows.find((r: any) => {
        const hasMetric = r.metricValues?.some(
          (mv: any) => metricKeysToMatch.includes(mv.key) || mv.key === cell.id,
        );
        if (!hasMetric) {
          if (cell.id === 'roas') {
            console.log('[KIPIFY_ROAS_DEBUG] Row does not have ROAS metric', {
              rowMetrics:
                r.metricValues?.map((mv: any) => `${mv.key}=${mv.value}`) || [],
              rowSplits:
                r.splitValues?.map((sv: any) => `${sv.key}=${sv.value}`) || [],
            });
          }
          return false;
        }

        // Se non ci sono split nella riga corrente, matcha qualsiasi riga con quella metrica
        if (currentSplits.length === 0) {
          if (cell.id === 'roas') {
            console.log(
              '[KIPIFY_ROAS_DEBUG] No current splits, matching any row with ROAS',
              {
                cellId: cell.id,
                rowSplits:
                  r.splitValues?.map((sv: any) => `${sv.key}=${sv.value}`) ||
                  [],
                rowMetrics:
                  r.metricValues?.map((mv: any) => `${mv.key}=${mv.value}`) ||
                  [],
              },
            );
          }
          return true;
        }

        // Se ci sono split, verifica che matchano
        const compareSplits = (r.splitValues || []).map((sv: any) => ({
          key: sv.key,
          value: String(sv.value ?? ''),
        }));

        // Se la riga di comparazione non ha split ma quella corrente sì,
        // potrebbe essere una metrica aggregata - matcha comunque per metrica
        if (compareSplits.length === 0) {
          if (cell.id === 'roas') {
            console.log(
              '[KIPIFY_ROAS_DEBUG] Compare row has no splits, matching by ROAS only (aggregated)',
              {
                cellId: cell.id,
                currentSplits,
                rowMetrics:
                  r.metricValues?.map((mv: any) => `${mv.key}=${mv.value}`) ||
                  [],
              },
            );
          }
          return true;
        }

        // Verifica che tutti gli split correnti siano presenti nella riga di comparazione
        const allSplitsMatch = currentSplits.every((currentSplit) =>
          compareSplits.some(
            (compareSplit: { key: string; value: string }) =>
              compareSplit.key === currentSplit.key &&
              compareSplit.value === currentSplit.value,
          ),
        );

        if (cell.id === 'roas' && !allSplitsMatch) {
          console.log('[KIPIFY_ROAS_DEBUG] Splits do not match for ROAS', {
            currentSplits,
            compareSplits,
            rowSplits:
              r.splitValues?.map((sv: any) => `${sv.key}=${sv.value}`) || [],
          });
        }

        return allSplitsMatch;
      });

      if (anyRowWithMetric) {
        const compareMetric = anyRowWithMetric.metricValues?.find(
          (mv: any) => metricKeysToMatch.includes(mv.key) || mv.key === cell.id,
        );
        if (compareMetric) {
          const numValue = Number(compareMetric.value);
          if (cell.id === 'roas') {
            console.log(
              '[KIPIFY_ROAS_DEBUG] ✅ Using fallback match for ROAS!',
              {
                cellId: cell.id,
                metricKey: compareMetric.key,
                value: compareMetric.value,
                numValue,
                currentSplitsCount: currentSplits.length,
                compareSplits:
                  anyRowWithMetric.splitValues?.map(
                    (sv: any) => `${sv.key}=${sv.value}`,
                  ) || [],
              },
            );
          }
          console.log('[KIPIFY_COMPARE_DEBUG] Using fallback match', {
            cellId: cell.id,
            metricKey: compareMetric.key,
            value: compareMetric.value,
            numValue,
            currentSplitsCount: currentSplits.length,
            compareSplits:
              anyRowWithMetric.splitValues?.map(
                (sv: any) => `${sv.key}=${sv.value}`,
              ) || [],
          });
          return !isNaN(numValue) ? numValue : null;
        } else if (cell.id === 'roas') {
          console.log(
            '[KIPIFY_ROAS_DEBUG] Row has metric but compareMetric not found',
            {
              cellId: cell.id,
              metricKeysToMatch,
              rowMetrics:
                anyRowWithMetric.metricValues?.map(
                  (mv: any) => `${mv.key}=${mv.value}`,
                ) || [],
            },
          );
        }
      } else {
        if (cell.id === 'roas') {
          console.log(
            '[KIPIFY_ROAS_DEBUG] ❌ No row found even with fallback for ROAS',
            {
              cellId: cell.id,
              metricKeysToMatch,
              compareRowsCount: compareRows.length,
              currentSplits,
              allRowsInfo: compareRows.map((r: any, idx: number) => ({
                index: idx,
                splits:
                  r.splitValues?.map((sv: any) => `${sv.key}=${sv.value}`) ||
                  [],
                metrics:
                  r.metricValues?.map((mv: any) => `${mv.key}=${mv.value}`) ||
                  [],
              })),
            },
          );
        }
        console.log('[KIPIFY_COMPARE_DEBUG] No row found even with fallback', {
          cellId: cell.id,
          metricKeysToMatch,
          compareRowsCount: compareRows.length,
          currentSplits,
        });
      }
    }

    return null;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Wrapper per il drag start che include il titolo della colonna
  const handleColumnDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    columnId: string,
  ) => {
    const column = findColumnByColumnId(safeRows[0], columnId);
    // Usa il titolo della colonna se disponibile, altrimenti converte l'ID in titolo leggibile
    const columnTitle = column?.title || getTitle(columnId);
    handleDragStart(event, columnId, columnTitle);
  };

  const handeColumnDelete = (value: TableRowsType) => {
    const metrics = getValues('metrics');
    const splits = getValues('splits');

    if (value.isSplit) {
      setValue(
        'splits',
        splits.filter(
          ({ key, connectorType }) =>
            !(
              connectorType === value.type &&
              cellsMatchColumnId(`${connectorType}::${key}`, value.columnId)
            ),
        ),
      );
    } else {
      setValue(
        'metrics',
        metrics.filter(
          ({ key, type }) =>
            !(
              type === value.type &&
              cellsMatchColumnId(`${type}::${key}`, value.columnId)
            ),
        ),
      );
    }

    changeColumnOrder(
      columnOrder.filter((column) => column !== value.columnId),
    );
  };

  // test
  const checkIsOnlyMetric = (value: TableRowsType) => {
    if (value.isSplit) return false;

    const metrics = getValues('metrics');

    return metrics.length === 1;
  };

  // Fallback per evitare errori su undefined/null
  const safeRows =
    Array.isArray(rows) &&
    rows.length > 0 &&
    Array.isArray(rows[0]) &&
    rows[0].length > 0
      ? rows
      : [[]];

  const {
    splits,
    shopifyMetrics,
    magentoMetrics,
    facebookMetrics,
    googleAdsMetrics,
    googleAnalyticsMetrics,
  } = useConnectors();

  const { data: customFormulasData } =
    useQuery<Pick<Query, 'getAllCustomFormulas'>>(GET_CUSTOM_FORMULAS);

  const mapFormatTypeToValueType = (
    formatType: MetricFormatType,
  ): ValueType => {
    switch (formatType) {
      case MetricFormatType.Currency:
        return ValueType.currency;
      case MetricFormatType.Percent:
        return ValueType.percentage;
      case MetricFormatType.Duration:
        return ValueType.duration;
      case MetricFormatType.Number:
      default:
        return ValueType.number;
    }
  };

  const transformedCustomFormulaData =
    customFormulasData?.getAllCustomFormulas?.map((el) => {
      return {
        id: el.id,
        label: el.name,
        key: el.id,
        category: 'metric',
        type: MetricsByConnectorType.CUSTOM_FORMULA,
        valueType: mapFormatTypeToValueType(el.format.type),
      };
    });

  const metricValueTypeMap = useMemo<
    Record<ConnectorType | MetricsByConnectorType, Record<string, ValueType>>
  >(() => {
    const getSubMap = (metrics: Metric[]) =>
      metrics.reduce((acc: Record<string, ValueType>, metric) => {
        if (metric.key) {
          acc[metric.key] = metric.valueType;
        }

        return acc;
      }, {});

    const kipifyMetricTypes = {
      total_marketing_spend: ValueType.currency,
      markup: ValueType.number,
      roas: ValueType.number,
      acos: ValueType.percentage,
      acos_total: ValueType.percentage,
      oac: ValueType.currency,
      cac: ValueType.currency,
      cac_new_customers: ValueType.currency,
    };
    const result = {
      [ConnectorType.SHOPIFY]: getSubMap(shopifyMetrics),
      [ConnectorType.FACEBOOK]: getSubMap(facebookMetrics),
      [ConnectorType.GOOGLE_ADS]: getSubMap(googleAdsMetrics),
      [ConnectorType.GOOGlE_ANALYTICS4]: getSubMap(googleAnalyticsMetrics),
      [ConnectorType.GOOGLE_SEARCH_CONSOLE]: {
        clicks: ValueType.number,
        impressions: ValueType.number,
        ctr: ValueType.percentage,
        position: ValueType.number,
      },
      [ConnectorType.KIPIFY]: kipifyMetricTypes,
      [ConnectorType.DATABASE]: {}, // Database metrics will be handled differently
      [ConnectorType.MAGENTO]: getSubMap(magentoMetrics || []),
      [MetricsByConnectorType.KIPIFY]: kipifyMetricTypes,
      [MetricsByConnectorType.CUSTOM_FORMULA]: getSubMap(
        transformedCustomFormulaData || [],
      ),
    };

    return result;
  }, [
    facebookMetrics,
    googleAdsMetrics,
    googleAnalyticsMetrics,
    shopifyMetrics,
    magentoMetrics,
    transformedCustomFormulaData,
  ]);

  // Modifica il tipo di splitValueTypeMap
  const splitValueTypeMap = useMemo<
    Record<ConnectorType | MetricsByConnectorType, Record<string, ValueType>>
  >(() => {
    return splits.reduce(
      (
        acc: Record<
          ConnectorType | MetricsByConnectorType,
          Record<string, ValueType>
        >,
        { connectorType, key, valueType },
      ) => {
        if (connectorType && key && valueType && acc[connectorType]) {
          acc[connectorType][key] = valueType;
        }

        return acc;
      },
      {
        [ConnectorType.SHOPIFY]: {},
        [ConnectorType.FACEBOOK]: {},
        [ConnectorType.GOOGLE_ADS]: {},
        [ConnectorType.GOOGlE_ANALYTICS4]: {},
        [ConnectorType.GOOGLE_SEARCH_CONSOLE]: {},
        [ConnectorType.KIPIFY]: {}, // Aggiunto mapping per Kipify
        [ConnectorType.DATABASE]: {},
        [ConnectorType.MAGENTO]: {},
        [MetricsByConnectorType.KIPIFY]: {},
        [MetricsByConnectorType.CUSTOM_FORMULA]: {},
      },
    );
  }, [splits]);

  const filters = watch(InputName.filters);

  const metricFilters = filters.filter(
    ({ type }) => type === FrontendFilterType.MetricFilter,
  );

  const filteredRows = useMemo(
    () =>
      safeRows.reduce((acc: TableRowsType[][], row) => {
        const matchEveryFilters = metricFilters.every(
          ({ connectorType, key, values }) => {
            if (!values.length) {
              return true;
            }

            const currentCell = row.find(
              (cell) =>
                !cell.isSplit && cell.type === connectorType && cell.id === key,
            );

            const numericCellValue = Number(currentCell?.value);

            return (
              !isNaN(numericCellValue) &&
              checkValueMatchFilters(numericCellValue, values)
            );
          },
        );

        if (matchEveryFilters) {
          acc.push(row);
        }

        return acc;
      }, []),
    [safeRows, metricFilters],
  );

  const filteredColumnOrder = useMemo(() => {
    const seen = new Set();
    if (!safeRows[0] || safeRows[0].length === 0) {
      return [];
    }

    return columnOrder.filter((colId) => {
      const column = findColumnByColumnId(safeRows[0], colId);
      if (!column) return false;

      // Per le colonne split, controlla duplicati basandosi su titolo + tipo
      if (column.isSplit) {
        if (
          !column.title ||
          column.title === 'null' ||
          seen.has(column.title + column.type)
        )
          return false;
        seen.add(column.title + column.type);
      }

      // Per le metriche, permette sempre colonne con ID diversi
      // anche se hanno lo stesso titolo (es. "Clicks" da Facebook e Google Ads)
      return true;
    });
  }, [columnOrder, safeRows]);

  const sortedRows = useMemo(() => {
    // Prima ordino le righe secondo l'ordine delle colonne definito dall'utente
    const orderedRows = filteredRows.map((row) => {
      const orderedRow = filteredColumnOrder
        .map((colId) => findCellByColumnId(row as TableRowsType[], colId))
        .filter(Boolean);
      return orderedRow;
    });

    // Poi applico l'ordinamento per colonna se specificato
    if (!sortConfig.columnId) return orderedRows;
    const column = findColumnByColumnId(safeRows[0], sortConfig.columnId);
    if (!column || column.isSplit) return orderedRows;

    return [...orderedRows].sort((a, b) => {
      const aCell = findCellByColumnId(
        a as TableRowsType[],
        sortConfig.columnId,
      );
      const bCell = findCellByColumnId(
        b as TableRowsType[],
        sortConfig.columnId,
      );
      const aValue = Number(aCell?.value);
      const bValue = Number(bCell?.value);
      if (isNaN(aValue) || isNaN(bValue)) return 0;
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [filteredRows, sortConfig, safeRows, filteredColumnOrder]);

  const hasVisibleSplitColumns = filteredColumnOrder.some((colId) => {
    const column = findColumnByColumnId(safeRows[0], colId);
    return !!column?.isSplit;
  });
  const showTotalsOnly = !hasVisibleSplitColumns;
  const paginationCount = showTotalsOnly ? 1 : filteredRows.length;
  const pages = Math.ceil(paginationCount / rowsPerPage);
  const currentPage = Math.max(0, Math.min(page, pages - 1));
  const offset = currentPage * rowsPerPage;
  const currentPageRows = useMemo(() => {
    if (showTotalsOnly) return [];
    return sortedRows.slice(offset, offset + rowsPerPage);
  }, [sortedRows, offset, rowsPerPage, showTotalsOnly]);

  // Sostituisco:
  // {filteredRows.length > 1000 && (
  //   <Box ...> ... </Box>
  // )}
  // con niente (rimuovo il warning)

  // Sostituisco:
  // <TablePagination ... count={Math.min(filteredRows.length, 1000)} ... />
  // con:
  // <TablePagination ... count={filteredRows.length} ... />
  // Parsing numerico robusto per totali (supporta formato IT "1.234,56" e "€ 1.234,56")
  const parseCellValueForTotal = (raw: unknown): number | null => {
    if (raw === null || raw === undefined) return null;
    const s = String(raw).trim();
    if (!s || s === '-') return null;
    const num = Number(raw);
    if (!isNaN(num)) return num;
    let cleaned = s.replace(/[^\d,.-]/g, '');
    if (!cleaned) return null;
    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');
    if (hasComma && hasDot)
      cleaned = cleaned.replace(/\./g, '').replace(/,/g, '.');
    else if (hasComma && !hasDot) cleaned = cleaned.replace(/,/g, '.');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  const getTotalDataForColumn = (columnIdx: string) => {
    let total = 0;
    filteredRows.forEach((row) => {
      const cell = findCellByColumnId(row as TableRowsType[], columnIdx);
      if (!cell || cell.isSplit) return;
      const n = parseCellValueForTotal(cell.value);
      if (n !== null) total += n;
    });
    return total.toFixed(2);
  };

  // Per la riga Totale: se il match per-split del confronto fallisce, calcoliamo un totale
  // aggregato direttamente dai dati di confronto (evita % enormi e confronto "mancante").
  const getCompareRowsForConnector = (
    connectorType: ConnectorType | MetricsByConnectorType,
  ): any[] => {
    if (!compareData) return [];

    if (connectorType === ConnectorType.SHOPIFY && compareData.shopify) {
      const shopifyData = compareData.shopify as any;
      if (
        shopifyData &&
        typeof shopifyData === 'object' &&
        'metricRows' in shopifyData
      ) {
        return Array.isArray(shopifyData.metricRows)
          ? shopifyData.metricRows
          : [];
      }
      return Array.isArray(shopifyData) ? shopifyData : [];
    }

    if (connectorType === ConnectorType.MAGENTO && compareData.magento) {
      const magentoData = compareData.magento as any;
      if (
        magentoData &&
        typeof magentoData === 'object' &&
        'metricRows' in magentoData
      ) {
        return Array.isArray(magentoData.metricRows)
          ? magentoData.metricRows
          : [];
      }
      return Array.isArray(magentoData) ? magentoData : [];
    }

    if (connectorType === ConnectorType.FACEBOOK && compareData.facebook) {
      return Array.isArray(compareData.facebook) ? compareData.facebook : [];
    }

    if (connectorType === ConnectorType.GOOGLE_ADS && compareData.googleAds) {
      return Array.isArray(compareData.googleAds) ? compareData.googleAds : [];
    }

    if (
      (connectorType === ConnectorType.KIPIFY ||
        (connectorType as any) === MetricsByConnectorType.KIPIFY) &&
      compareData.kipify
    ) {
      return Array.isArray(compareData.kipify) ? compareData.kipify : [];
    }

    if (
      connectorType === ConnectorType.GOOGlE_ANALYTICS4 &&
      compareData.googleAnalytics4
    ) {
      return Array.isArray(compareData.googleAnalytics4)
        ? compareData.googleAnalytics4
        : [];
    }

    if (
      connectorType === ConnectorType.GOOGLE_SEARCH_CONSOLE &&
      compareData.googleSearchConsole
    ) {
      return Array.isArray(compareData.googleSearchConsole)
        ? compareData.googleSearchConsole
        : [];
    }

    if (connectorType === ConnectorType.DATABASE && compareData.database) {
      return Array.isArray(compareData.database) ? compareData.database : [];
    }

    return [];
  };

  const buildMetricKeysToMatchForLooseSum = (cell: TableRowsType): string[] => {
    const keys: string[] = [];
    if (!cell?.id) return keys;
    keys.push(cell.id);

    if (cell.type === ConnectorType.GOOGLE_ADS) {
      const k = String(cell.id).toLowerCase();
      if (k === 'cost' || k === 'spend') {
        keys.push('metrics.cost_micros', 'cost_micros', 'metrics.cost', 'cost');
      } else if (k === 'roas') {
        keys.push('metrics.roas', 'roas');
      }
      if (!cell.id.startsWith('metrics.')) keys.push(`metrics.${cell.id}`);
      if (cell.id.startsWith('metrics.'))
        keys.push(cell.id.replace(/^metrics\./, ''));
    }

    // Kipify: API restituisce key "total_marketing_spend"; la colonna può avere id "total_marketing_spend" o "kipify_total_marketing_spend"
    if (
      cell.type === ConnectorType.KIPIFY ||
      (cell.type as any) === MetricsByConnectorType.KIPIFY
    ) {
      const k = String(cell.id).toLowerCase();
      if (
        k.includes('total_marketing_spend') ||
        k === 'kipify_total_marketing_spend'
      ) {
        keys.push('total_marketing_spend', 'kipify_total_marketing_spend');
      }
    }

    return Array.from(new Set(keys));
  };

  const sumCompareMetricLoose = (cell: TableRowsType): number | null => {
    const rows = getCompareRowsForConnector(
      cell.type as ConnectorType | MetricsByConnectorType,
    );
    if (!rows.length) return null;
    const keys = buildMetricKeysToMatchForLooseSum(cell);
    if (!keys.length) return null;

    let sum = 0;
    let found = false;
    rows.forEach((r: any) => {
      const metricValues = Array.isArray(r?.metricValues) ? r.metricValues : [];
      metricValues.forEach((mv: any) => {
        if (!mv) return;
        if (!keys.includes(mv.key)) return;
        const n = parseCellValueForTotal(mv.value);
        if (n !== null) {
          sum += n;
          found = true;
        }
      });
    });
    return found ? sum : null;
  };

  const isGoogleAdsRoasColumn = (cell?: TableRowsType): boolean =>
    !!cell &&
    !cell.isSplit &&
    cell.type === ConnectorType.GOOGLE_ADS &&
    (cell.id === 'roas' || cell.id === 'metrics.roas');

  const isGoogleAdsCostCell = (cell?: TableRowsType): boolean => {
    if (!cell || cell.isSplit || cell.type !== ConnectorType.GOOGLE_ADS)
      return false;
    const hay = `${cell.id ?? ''} ${cell.title ?? ''}`.toLowerCase();
    return /metrics\.cost_micros|cost_micros|metrics\.cost|\bcost\b|\bspend\b|advertising_spend/.test(
      hay,
    );
  };

  const isGoogleAdsConversionValueCell = (cell?: TableRowsType): boolean => {
    if (!cell || cell.isSplit || cell.type !== ConnectorType.GOOGLE_ADS)
      return false;
    const hay = `${cell.id ?? ''} ${cell.title ?? ''}`.toLowerCase();
    return /metrics\.conversions_value|conversions_value|\brevenue\b/.test(hay);
  };

  const sumCurrentRowsByMatcher = (
    matcher: (cell?: TableRowsType) => boolean,
  ): number | null => {
    let sum = 0;
    let found = false;
    filteredRows.forEach((row) => {
      row.forEach((cell) => {
        if (!matcher(cell)) return;
        const n = parseCellValueForTotal(cell.value);
        if (n !== null) {
          sum += n;
          found = true;
        }
      });
    });
    return found ? sum : null;
  };

  const sumCompareRowsByMatcher = (
    connectorType: ConnectorType | MetricsByConnectorType,
    matcher: (metricKey: string) => boolean,
  ): number | null => {
    const rows = getCompareRowsForConnector(connectorType);
    if (!rows.length) return null;
    let sum = 0;
    let found = false;
    rows.forEach((row: any) => {
      const metricValues = Array.isArray(row?.metricValues)
        ? row.metricValues
        : [];
      metricValues.forEach((mv: any) => {
        if (!mv?.key || !matcher(String(mv.key))) return;
        const n = parseCellValueForTotal(mv.value);
        if (n !== null) {
          sum += n;
          found = true;
        }
      });
    });
    return found ? sum : null;
  };

  const getConnectorIcon = (type: ConnectorType) => {
    switch (type) {
      case ConnectorType.SHOPIFY:
        return <ShopifyIcon style={{ width: 18, height: 18 }} />;
      case ConnectorType.FACEBOOK:
        return <FacebookIcon style={{ width: 18, height: 18 }} />;
      case ConnectorType.GOOGLE_ADS:
        return <GoogleAdsIcon style={{ width: 18, height: 18 }} />;
      case ConnectorType.GOOGlE_ANALYTICS4:
        return <GoogleAnalyticsIcon style={{ width: 18, height: 18 }} />;
      case ConnectorType.GOOGLE_SEARCH_CONSOLE:
        return <GoogleSearchConsoleIcon style={{ width: 18, height: 18 }} />;
      case ConnectorType.KIPIFY:
        return (
          <div
            style={{
              width: '18px',
              height: '18px',
              backgroundColor: '#222822',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1EED9F',
              fontSize: '10px',
              fontWeight: 'bold',
            }}
          >
            K
          </div>
        );
      case ConnectorType.DATABASE:
        return (
          <div
            style={{
              width: '18px',
              height: '18px',
              backgroundColor: '#1976d2',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '10px',
              fontWeight: 'bold',
            }}
          >
            DB
          </div>
        );
      case ConnectorType.MAGENTO:
        return (
          <div
            style={{
              width: '18px',
              height: '18px',
              backgroundColor: '#ee6723',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '10px',
              fontWeight: 'bold',
            }}
          >
            M
          </div>
        );
      default:
        return null;
    }
  };

  // PATCH: Mostra riga aggregata e totale se c'è solo uno split e un solo valore
  const onlyOneSplit = useMemo(() => {
    // Trova colonne split
    const splitColumns = filteredColumnOrder.filter((colId) => {
      const column = findColumnByColumnId(safeRows[0], colId);
      return column?.isSplit;
    });
    // Se c'è solo uno split e tutte le righe hanno lo stesso valore per quello split
    // MA non nascondere mai le colonne split per permettere sempre la visibilità
    if (splitColumns.length === 1) {
      // Rimuovo la condizione che nasconde le colonne split
      // if (uniqueSplitValues.size === 1) {
      //   return true;
      // }
    }
    return false;
  }, [filteredColumnOrder, safeRows, filteredRows]);

  return (
    <Wrapper
      sx={{ maxHeight: 'calc(100% - 2px)' }}
      isshortwidth={metricsIsShown ? 'active' : ''}
    >
      <Box style={{ overflowY: 'auto' }} className="tableContainer">
        <Table>
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3,
              backgroundColor: palette.grey[200],
            }}
          >
            <TableRow sx={{ backgroundColor: palette.grey[200] }}>
              {filteredColumnOrder.map((colId) => {
                const column = findColumnByColumnId(safeRows[0], colId);
                if (!column) return null;
                // Rimuovo la logica che nasconde le colonne split
                // if (onlyOneSplit && column.isSplit) return null;
                const isMetric = !column.isSplit;
                return (
                  <CustomHeadTableCell
                    key={column.columnId}
                    isborderright="active"
                    data-column-id={column.columnId}
                    className={`
                      ${sortConfig.columnId === column.columnId ? 'sorted' : ''}
                      ${getColumnClassName(column.columnId)}
                      ${dragState.isDragging ? 'drag-mode' : ''}
                    `.trim()}
                    onDragOver={(event) =>
                      handleDragOver(event, column.columnId)
                    }
                    onDragLeave={(event) =>
                      handleDragLeave(event, column.columnId)
                    }
                    onDrop={(event) => handleDrop(event, column.columnId)}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap="20px"
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        gap="5px"
                      >
                        <DragIndicatorWrapper
                          onDragStart={(event) =>
                            handleColumnDragStart(event, column.columnId)
                          }
                          onDragEnd={handleDragEnd}
                          onDrag={(event) => handleDrag(event)}
                          sx={{
                            cursor: dragState.isDragging ? 'grabbing' : 'grab',
                            transform:
                              dragState.isDragging &&
                              dragState.draggedId === column.columnId
                                ? 'scale(1.2)'
                                : 'scale(1)',
                            transition: 'transform 0.2s ease',
                          }}
                          draggable
                          role="button"
                          aria-label={`Trascina colonna ${column.title} per riordinare`}
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              // Simula l'inizio del drag per accessibilità
                              const dragEvent = new DragEvent('dragstart', {
                                dataTransfer: new DataTransfer(),
                              });
                              handleDragStart(
                                dragEvent as any,
                                column.columnId,
                              );
                            }
                          }}
                        >
                          <DragIndicatorIcon />
                        </DragIndicatorWrapper>
                        {column.isSplit &&
                        Object.values(ConnectorType).includes(
                          column.type as ConnectorType,
                        )
                          ? getConnectorIcon(column.type as ConnectorType)
                          : column.icon}
                        <Title>{column.title}</Title>
                        {isMetric && (
                          <SortIconWrapper
                            active={sortConfig.columnId === column.columnId}
                            tabIndex={0}
                            role="button"
                            aria-label={`Ordina per ${column.title}`}
                            onClick={() => {
                              updateSortConfig((prev) => {
                                if (prev.columnId === column.columnId) {
                                  return {
                                    columnId: column.columnId,
                                    direction:
                                      prev.direction === 'asc' ? 'desc' : 'asc',
                                  };
                                }
                                return {
                                  columnId: column.columnId,
                                  direction: 'desc',
                                };
                              });
                            }}
                          >
                            {sortConfig.columnId === column.columnId ? (
                              sortConfig.direction === 'asc' ? (
                                <ArrowUpward sx={{ fontSize: 18 }} />
                              ) : (
                                <ArrowDownward sx={{ fontSize: 18 }} />
                              )
                            ) : (
                              <ArrowDownward
                                sx={{ fontSize: 18, opacity: 0.3 }}
                              />
                            )}
                          </SortIconWrapper>
                        )}
                      </Box>
                      {!checkIsOnlyMetric(column) && (
                        <DeleteIconButton
                          onClick={() => handeColumnDelete(column)}
                        >
                          <DeleteIcon />
                        </DeleteIconButton>
                      )}
                    </Box>
                    {dragState.dropZoneId === column.columnId && (
                      <DropZoneIndicator className="active" />
                    )}
                  </CustomHeadTableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* PATCH: Se solo uno split e un solo valore, mostra una sola riga aggregata */}
            {onlyOneSplit ? (
              <TableRow key="aggregatedRowKey" sx={{ height: '36px' }}>
                {filteredColumnOrder.map((colId) => {
                  const column = findColumnByColumnId(safeRows[0], colId);
                  if (!column) return null;
                  const cell = findCellByColumnId(
                    filteredRows[0] as TableRowsType[],
                    colId,
                  );
                  const numCellValue = Number(cell?.value);
                  const cellType =
                    cell &&
                    (cell.isSplit ? splitValueTypeMap : metricValueTypeMap)[
                      cell.type as ConnectorType | MetricsByConnectorType
                    ]?.[cell.id];

                  // Calcola variazione se c'è comparazione e la cella è una metrica (per la riga del totale)
                  const compareValue =
                    !cell?.isSplit && compareDateRange && cell
                      ? findCompareValue(
                          cell,
                          filteredRows[0].filter(
                            (c): c is TableRowsType => c !== undefined,
                          ),
                        )
                      : null;
                  const change =
                    compareValue !== null && !isNaN(numCellValue)
                      ? calculateChange(
                          numCellValue,
                          compareValue,
                          cell?.id,
                          cell?.type,
                        )
                      : null;

                  return (
                    <CustomBodyTableCell key={colId} isborderright="active">
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Title>
                          {cell && !isNaN(numCellValue) ? (
                            formatCustomReportCellValue({
                              value: cell.value,
                              connectorType: cell.type,
                              isSplit: cell.isSplit,
                              cellType,
                            })
                          ) : cell &&
                            cellType === ValueType.image &&
                            typeof cell.value === 'string' &&
                            cell.value !== notApplicableValue ? (
                            <img src={cell.value} alt="split image" />
                          ) : (
                            cell?.value ?? ''
                          )}
                        </Title>
                        {change &&
                          compareDateRange &&
                          !cell?.isSplit &&
                          !(
                            (cell?.id === 'roas' ||
                              cell?.id === 'kipify_roas') &&
                            (cell?.value === '0.00' ||
                              cell?.value === 0 ||
                              cell?.value === '0') &&
                            'isInfinite' in change &&
                            change.isInfinite
                          ) && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontSize: '0.75rem',
                              }}
                            >
                              {change.isPositive ? (
                                <TrendingUpIcon
                                  sx={{ color: 'success.main', fontSize: 14 }}
                                />
                              ) : (
                                <TrendingDownIcon
                                  sx={{ color: 'warning.main', fontSize: 14 }}
                                />
                              )}
                              <Typography
                                variant="caption"
                                color={
                                  change.isPositive
                                    ? 'success.main'
                                    : 'warning.main'
                                }
                                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                              >
                                {change.isPositive ? '+' : ''}
                                {'isInfinite' in change && change.isInfinite
                                  ? '∞'
                                  : `${change.percentage}%`}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.7rem',
                                  opacity: 0.7,
                                  marginLeft: '4px',
                                }}
                              >
                                (
                                {formatCustomReportCellValue({
                                  value: String(change.value),
                                  connectorType: cell?.type,
                                  isSplit: cell?.isSplit,
                                  cellType,
                                })}
                                )
                              </Typography>
                            </Box>
                          )}
                      </Box>
                    </CustomBodyTableCell>
                  );
                })}
              </TableRow>
            ) : showTotalsOnly ? null : (
              currentPageRows.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    backgroundColor:
                      idx % 2 !== 0 ? palette.secondary.main : undefined,
                    height: '36px',
                  }}
                >
                  {row.map((cell) => {
                    if (!cell) return null;
                    const numCellValue = Number(cell.value);
                    const cellType =
                      cell &&
                      (cell.isSplit ? splitValueTypeMap : metricValueTypeMap)[
                        cell.type as ConnectorType | MetricsByConnectorType
                      ]?.[cell.id];

                    // Calcola variazione se c'è comparazione e la cella è una metrica
                    const compareValue =
                      !cell.isSplit && compareDateRange
                        ? findCompareValue(
                            cell,
                            row.filter(
                              (c): c is TableRowsType => c !== undefined,
                            ),
                          )
                        : null;
                    const change =
                      compareValue !== null && !isNaN(numCellValue)
                        ? calculateChange(
                            numCellValue,
                            compareValue,
                            cell.id,
                            cell.type,
                          )
                        : null;

                    return (
                      <CustomBodyTableCell
                        key={cell.columnId}
                        isborderright="active"
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          <Title>
                            {!isNaN(numCellValue) && cell ? (
                              formatCustomReportCellValue({
                                value: cell.value,
                                connectorType: cell.type,
                                isSplit: cell.isSplit,
                                cellType,
                              })
                            ) : cell &&
                              cellType === ValueType.image &&
                              cell.value &&
                              typeof cell.value === 'string' &&
                              cell.value !== notApplicableValue ? (
                              <img src={cell.value} alt="split image" />
                            ) : (
                              cell?.value ?? ''
                            )}
                          </Title>
                          {change &&
                            compareDateRange &&
                            !cell.isSplit &&
                            !(
                              (cell.id === 'roas' ||
                                cell.id === 'kipify_roas') &&
                              (cell.value === '0.00' ||
                                cell.value === 0 ||
                                cell.value === '0') &&
                              'isInfinite' in change &&
                              change.isInfinite
                            ) && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  fontSize: '0.75rem',
                                }}
                              >
                                {change.isPositive ? (
                                  <TrendingUpIcon
                                    sx={{ color: 'success.main', fontSize: 14 }}
                                  />
                                ) : (
                                  <TrendingDownIcon
                                    sx={{ color: 'warning.main', fontSize: 14 }}
                                  />
                                )}
                                <Typography
                                  variant="caption"
                                  color={
                                    change.isPositive
                                      ? 'success.main'
                                      : 'warning.main'
                                  }
                                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                                >
                                  {change.isPositive ? '+' : ''}
                                  {'isInfinite' in change && change.isInfinite
                                    ? '∞'
                                    : `${change.percentage}%`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.7rem',
                                    opacity: 0.7,
                                    marginLeft: '4px',
                                  }}
                                >
                                  (
                                  {formatCustomReportCellValue({
                                    value: String(change.value),
                                    connectorType: cell?.type,
                                    isSplit: cell?.isSplit,
                                    cellType,
                                  })}
                                  )
                                </Typography>
                              </Box>
                            )}
                        </Box>
                      </CustomBodyTableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
            {/* Riga totale sempre visibile */}
            <TableRow
              key="totalRowKey"
              sx={{
                height: '36px',
                background: palette.grey[100],
                borderTop: `2.5px solid ${palette.primary.main}`,
              }}
            >
              {filteredColumnOrder.map((colId, idx) => {
                const column = findColumnByColumnId(safeRows[0], colId);
                const isKipifyRoasTotal =
                  !!column &&
                  !column.isSplit &&
                  (column.id === 'roas' || column.id === 'kipify_roas') &&
                  (column.type === ConnectorType.KIPIFY ||
                    (column.type as any) === MetricsByConnectorType.KIPIFY);
                const isGoogleAdsRoasTotal = isGoogleAdsRoasColumn(column);
                const isRoasTotal = isKipifyRoasTotal || isGoogleAdsRoasTotal;

                const isKipifyMarkupTotalCol =
                  !!column &&
                  !column.isSplit &&
                  (column.id === 'markup' || column.id === 'kipify_markup') &&
                  (column.type === ConnectorType.KIPIFY ||
                    (column.type as any) === MetricsByConnectorType.KIPIFY);

                const isKipifyAcosTotalCol =
                  !!column &&
                  !column.isSplit &&
                  (column.id === 'acos' || column.id === 'kipify_acos') &&
                  (column.type === ConnectorType.KIPIFY ||
                    (column.type as any) === MetricsByConnectorType.KIPIFY);

                const isKipifyAcosShippingTotalCol =
                  !!column &&
                  !column.isSplit &&
                  (column.id === 'acos_total' ||
                    column.id === 'kipify_acos_total') &&
                  (column.type === ConnectorType.KIPIFY ||
                    (column.type as any) === MetricsByConnectorType.KIPIFY);

                const isKipifyOacTotalCol =
                  !!column &&
                  !column.isSplit &&
                  column.id === 'oac' &&
                  (column.type === ConnectorType.KIPIFY ||
                    (column.type as any) === MetricsByConnectorType.KIPIFY);

                const isKipifyCacTotalCol =
                  !!column &&
                  !column.isSplit &&
                  column.id === 'cac' &&
                  (column.type === ConnectorType.KIPIFY ||
                    (column.type as any) === MetricsByConnectorType.KIPIFY);

                const isKipifyCacNewCustomersTotalCol =
                  !!column &&
                  !column.isSplit &&
                  column.id === 'cac_new_customers' &&
                  (column.type === ConnectorType.KIPIFY ||
                    (column.type as any) === MetricsByConnectorType.KIPIFY);

                const isKipifyRoasHelperTotalCol =
                  !!column &&
                  !column.isSplit &&
                  (column.id === 'roas' || column.id === 'kipify_roas') &&
                  (column.type === ConnectorType.KIPIFY ||
                    (column.type as any) === MetricsByConnectorType.KIPIFY);

                const kipifyTotalHelperMetricKey = isKipifyMarkupTotalCol
                  ? 'markup'
                  : isKipifyRoasHelperTotalCol
                    ? 'roas'
                    : isKipifyAcosTotalCol
                      ? 'acos'
                      : isKipifyAcosShippingTotalCol
                        ? 'acos_total'
                        : isKipifyOacTotalCol
                          ? 'oac'
                          : isKipifyCacTotalCol
                            ? 'cac'
                            : isKipifyCacNewCustomersTotalCol
                              ? 'cac_new_customers'
                              : null;

                const kipifyTotalHelperRow =
                  kipifyTotalHelperMetricKey &&
                  Array.isArray(kipifyRowsForTotals) &&
                  kipifyRowsForTotals.length > 0
                    ? kipifyRowsForTotals.find(
                        (r: any) =>
                          (r?.splitValues || []).some(
                            (sv: any) => sv?.key === '_is_total_row',
                          ) &&
                          (r?.metricValues || []).some(
                            (mv: any) => mv?.key === kipifyTotalHelperMetricKey,
                          ),
                      )
                    : null;

                const kipifyHelperTotalValue = kipifyTotalHelperMetricKey
                  ? kipifyTotalHelperRow?.metricValues?.find(
                      (mv: any) => mv?.key === kipifyTotalHelperMetricKey,
                    )?.value ?? null
                  : null;

                const cellValue = column?.isSplit
                  ? ''
                  : kipifyHelperTotalValue != null &&
                      String(kipifyHelperTotalValue).trim() !== ''
                    ? String(kipifyHelperTotalValue)
                    : getTotalDataForColumn(colId);
                const cellType =
                  column &&
                  (column.isSplit ? splitValueTypeMap : metricValueTypeMap)[
                    column.type as ConnectorType | MetricsByConnectorType
                  ]?.[column.id];

                // Comparazione anche sulla riga Totale: per metriche additive (spend, revenue)
                // usiamo la somma di TUTTE le righe del periodo di confronto (sumCompareMetricLoose)
                // così il totale di confronto è coerente con la somma delle righe e non dipende dal match per-split.
                const numTotalRaw = Number(cellValue);
                const numTotal = !isNaN(numTotalRaw)
                  ? numTotalRaw
                  : parseCellValueForTotal(cellValue) ?? NaN;
                let compareTotal: number | null = null;
                const isAdditiveMetric =
                  (column &&
                    !column.isSplit &&
                    (column.type === ConnectorType.KIPIFY ||
                      (column.type as any) === MetricsByConnectorType.KIPIFY) &&
                    /total_marketing_spend/i.test(
                      column.id || column.title || '',
                    )) ||
                  (column?.type === ConnectorType.FACEBOOK &&
                    /spend|amount/i.test(column.id || column.title || '')) ||
                  (column?.type === ConnectorType.GOOGLE_ADS &&
                    /cost|spend/i.test(column.id || column.title || '')) ||
                  (column?.type === ConnectorType.SHOPIFY &&
                    /sales|revenue/i.test(column.id || column.title || '')) ||
                  (column?.type === ConnectorType.GOOGlE_ANALYTICS4 &&
                    /revenue|purchase|items|sessions|users|eventcount|views/i.test(
                      column.id || column.title || '',
                    )) ||
                  // GSC: clicks/impressions additive; position NO (media).
                  (column?.type === ConnectorType.GOOGLE_SEARCH_CONSOLE &&
                    /^(clicks|impressions)$/i.test(
                      column.id || column.title || '',
                    ));

                if (!column?.isSplit && compareDateRange) {
                  const kipifyCompareRows = compareData?.kipify;
                  if (Array.isArray(kipifyCompareRows)) {
                    const cmpKey = kipifyTotalHelperMetricKey;

                    if (cmpKey) {
                      const cmpAgg = kipifyCompareRows.find(
                        (r: any) =>
                          (r?.splitValues || []).some(
                            (sv: any) => sv?.key === '_is_total_row',
                          ) &&
                          (r?.metricValues || []).some(
                            (mv: any) => mv?.key === cmpKey,
                          ),
                      );
                      const cmpMv = cmpAgg?.metricValues?.find(
                        (mv: any) => mv?.key === cmpKey,
                      )?.value;

                      if (cmpMv != null && String(cmpMv).trim() !== '') {
                        compareTotal = parseCellValueForTotal(cmpMv);
                      }
                    }
                  }
                  // Per metriche additive preferisci il totale da compareData (somma di tutte le righe)
                  if (compareTotal === null && isAdditiveMetric && column) {
                    compareTotal = sumCompareMetricLoose(column);
                  }
                  // Guard-rail come Facebook: mai forEach findCompareValue su migliaia di
                  // righe (O(n²) → tab freeze). Solo somma loose o skip.
                  if (compareTotal === null) {
                    const tooManyRows =
                      filteredRows.length > 200 ||
                      (gscCompareRows?.length ?? 0) > 200 ||
                      (facebookCompareRows?.length ?? 0) > 200;
                    if (tooManyRows) {
                      compareTotal = column
                        ? sumCompareMetricLoose(column)
                        : null;
                    } else {
                      let sum = 0;
                      filteredRows.forEach((r) => {
                        const metricCell = findCellByColumnId(
                          r as TableRowsType[],
                          colId,
                        );
                        if (!metricCell || metricCell.isSplit) return;
                        const v = findCompareValue(
                          metricCell,
                          r.filter((c): c is TableRowsType => c !== undefined),
                        );
                        const parsed = parseCellValueForTotal(v);
                        if (parsed !== null) sum += parsed;
                      });
                      compareTotal = sum;
                    }
                  }
                }
                let totalDisplayValue: string | number = cellValue;
                let totalChange =
                  !column?.isSplit &&
                  compareDateRange &&
                  !isNaN(numTotal) &&
                  compareTotal !== null
                    ? calculateChange(
                        numTotal,
                        compareTotal,
                        column?.id,
                        column?.type as any,
                      )
                    : null;

                // Fallback: se ancora nessun compareTotal (es. nessun match), prova sumCompareMetricLoose
                if (
                  totalChange === null &&
                  compareDateRange &&
                  !column?.isSplit &&
                  column &&
                  compareTotal === null
                ) {
                  const loose = sumCompareMetricLoose(column);
                  if (loose !== null && !isNaN(numTotal)) {
                    compareTotal = loose;
                    totalChange = calculateChange(
                      numTotal,
                      loose,
                      column.id,
                      column.type as any,
                    );
                  }
                }

                if (
                  isKipifyRoasTotal &&
                  (kipifyHelperTotalValue == null ||
                    String(kipifyHelperTotalValue).trim() === '')
                ) {
                  // Totali corrente (ratio revenue/spend)
                  let totalRevenue = 0;
                  let totalSpend = 0;

                  filteredColumnOrder.forEach((cid) => {
                    const c = findColumnByColumnId(safeRows[0], cid);
                    if (!c || c.isSplit) return;
                    if (
                      c.type === ConnectorType.SHOPIFY &&
                      /gross.*sales|total.*sales|net.*sales|revenue|sales/i.test(
                        c.id || c.title || '',
                      )
                    ) {
                      const v =
                        parseCellValueForTotal(getTotalDataForColumn(cid)) ??
                        Number(getTotalDataForColumn(cid));
                      if (v !== null && !isNaN(v)) totalRevenue += v;
                    }
                    if (
                      (c.type === ConnectorType.FACEBOOK &&
                        /spend|amount_spent|amount/i.test(
                          c.id || c.title || '',
                        )) ||
                      (c.type === ConnectorType.GOOGLE_ADS &&
                        /cost|spend|advertising_spend/i.test(
                          c.id || c.title || '',
                        )) ||
                      ((c.type === ConnectorType.KIPIFY ||
                        (c.type as any) === MetricsByConnectorType.KIPIFY) &&
                        /total_marketing_spend/i.test(c.id || c.title || ''))
                    ) {
                      const v =
                        parseCellValueForTotal(getTotalDataForColumn(cid)) ??
                        Number(getTotalDataForColumn(cid));
                      if (v !== null && !isNaN(v)) totalSpend += v;
                    }
                  });

                  const roas = totalSpend > 0 ? totalRevenue / totalSpend : NaN;
                  totalDisplayValue = isFinite(roas) ? roas.toFixed(2) : '-';

                  // Totali confronto
                  if (compareDateRange) {
                    let compareRevenueSum = 0;
                    let compareSpendSum = 0;
                    // Usa la stessa logica del periodo corrente: somma per-colonna, con fallback senza split.
                    filteredColumnOrder.forEach((cid) => {
                      const c = findColumnByColumnId(safeRows[0], cid);
                      if (!c || c.isSplit) return;

                      if (
                        c.type === ConnectorType.SHOPIFY &&
                        /gross.*sales|total.*sales|net.*sales|revenue|sales/i.test(
                          c.id || c.title || '',
                        )
                      ) {
                        const loose = sumCompareMetricLoose(c);
                        if (loose !== null) compareRevenueSum += loose;
                      }

                      if (
                        (c.type === ConnectorType.FACEBOOK &&
                          /spend|amount_spent|amount/i.test(
                            c.id || c.title || '',
                          )) ||
                        (c.type === ConnectorType.GOOGLE_ADS &&
                          /cost|spend|advertising_spend/i.test(
                            c.id || c.title || '',
                          )) ||
                        ((c.type === ConnectorType.KIPIFY ||
                          (c.type as any) === MetricsByConnectorType.KIPIFY) &&
                          /total_marketing_spend/i.test(c.id || c.title || ''))
                      ) {
                        const loose = sumCompareMetricLoose(c);
                        if (loose !== null) compareSpendSum += loose;
                      }
                    });
                    const compareRoas =
                      compareSpendSum > 0
                        ? compareRevenueSum / compareSpendSum
                        : NaN;
                    totalChange =
                      isFinite(roas) && isFinite(compareRoas)
                        ? calculateChange(
                            Number(roas),
                            Number(compareRoas.toFixed(2)),
                          )
                        : null;
                  }
                  // Non mostrare "+∞ (0)" quando ROAS è 0.00 (confronto fuorviante)
                  if (
                    isRoasTotal &&
                    totalChange &&
                    'isInfinite' in totalChange &&
                    totalChange.isInfinite &&
                    (String(totalDisplayValue) === '0.00' ||
                      totalDisplayValue === '0')
                  ) {
                    totalChange = null;
                  }
                }

                if (isGoogleAdsRoasTotal) {
                  const currentConversionsValue = sumCurrentRowsByMatcher(
                    isGoogleAdsConversionValueCell,
                  );
                  const currentCost =
                    sumCurrentRowsByMatcher(isGoogleAdsCostCell);
                  const roas =
                    currentCost &&
                    currentCost > 0 &&
                    currentConversionsValue !== null
                      ? currentConversionsValue / currentCost
                      : NaN;
                  totalDisplayValue = isFinite(roas) ? roas.toFixed(2) : '-';

                  if (compareDateRange) {
                    const compareConversionsValue = sumCompareRowsByMatcher(
                      ConnectorType.GOOGLE_ADS,
                      (key) =>
                        /metrics\.conversions_value|conversions_value|revenue/i.test(
                          key,
                        ),
                    );
                    const compareCost = sumCompareRowsByMatcher(
                      ConnectorType.GOOGLE_ADS,
                      (key) =>
                        /metrics\.cost_micros|cost_micros|metrics\.cost|\bcost\b|\bspend\b|advertising_spend/i.test(
                          key,
                        ),
                    );
                    const compareRoas =
                      compareCost &&
                      compareCost > 0 &&
                      compareConversionsValue !== null
                        ? compareConversionsValue / compareCost
                        : NaN;
                    totalChange =
                      isFinite(roas) && isFinite(compareRoas)
                        ? calculateChange(
                            Number(roas),
                            Number(compareRoas.toFixed(2)),
                          )
                        : null;
                  }
                }

                return (
                  <CustomBodyTableCell
                    key={`totalRow${idx}Key`}
                    isborderright="active"
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Title
                        sx={{
                          fontWeight: 800,
                          color: 'text.primary',
                          fontSize: '1.08em',
                        }}
                      >
                        {idx === 0 && hasVisibleSplitColumns
                          ? 'Total'
                          : isRoasTotal
                            ? totalDisplayValue === '-'
                              ? '-'
                              : formatCustomReportCellValue({
                                  value: Number(totalDisplayValue),
                                  connectorType: column?.type,
                                  isSplit: column?.isSplit,
                                  cellType,
                                })
                            : formatCustomReportCellValue({
                                value: cellValue,
                                connectorType: column?.type,
                                isSplit: column?.isSplit,
                                cellType,
                              })}
                      </Title>

                      {totalChange && compareDateRange && !column?.isSplit && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: '0.75rem',
                          }}
                        >
                          {totalChange.isPositive ? (
                            <TrendingUpIcon
                              sx={{ color: 'success.main', fontSize: 14 }}
                            />
                          ) : (
                            <TrendingDownIcon
                              sx={{ color: 'warning.main', fontSize: 14 }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            color={
                              totalChange.isPositive
                                ? 'success.main'
                                : 'warning.main'
                            }
                            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                          >
                            {totalChange.isPositive ? '+' : ''}
                            {'isInfinite' in totalChange &&
                            totalChange.isInfinite
                              ? '∞'
                              : `${totalChange.percentage}%`}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.7rem',
                              opacity: 0.7,
                              marginLeft: '4px',
                            }}
                          >
                            (
                            {formatCustomReportCellValue({
                              value: String(totalChange.value),
                              connectorType: column?.type,
                              isSplit: column?.isSplit,
                              cellType,
                            })}
                            )
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CustomBodyTableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      <Box
        sx={{
          minHeight: '52px',
          borderTop: '1px solid #DDDDEA',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <TablePagination
          sx={{
            minHeight: '52px',
            overflowY: 'hidden',
          }}
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={paginationCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      {dragState.dragShadow.visible && (
        <DragShadow
          style={{
            left: dragState.dragShadow.x,
            top: dragState.dragShadow.y,
            transform: `translate(-50%, -50%) rotate(${
              dragState.dragShadow.text.includes('Drag') ? '0deg' : '5deg'
            }) scale(1.05)`,
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.25)',
            opacity: 0.9,
          }}
        >
          {dragState.dragShadow.text}
        </DragShadow>
      )}
    </Wrapper>
  );
}
